import React, { memo, useEffect, useState } from "react";
import * as Sentry from '@sentry/react';

const PUNCTUATION_REGEX = /[,.:;!¡"'""]+$/;
const SPECIAL_CHARS_SYMBOLS = '@#$%&*()={}[]|\\:;"\'<>,/~^';

const SelectTextEditorSingleWord = ({
  initialText = "",
  highlights = [],
  setHighlights,
  from ="",
  stripPunctuation = false
}) => {
  const stripHtmlTags = (html) => {
    if (!html) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const getCleanWord = (word) => {
    if (!stripPunctuation) return word;
    let clean = word.replace(PUNCTUATION_REGEX, '');
    if (word.endsWith('_.') && clean.endsWith('_')) clean = clean + '.';
    return !clean && word ? word : clean;
  };

  const [text,setText] = useState(stripHtmlTags(initialText));

  useEffect(() => {
    setText(stripHtmlTags(initialText));
  }, [initialText]);

  const splitIntoWords = (text) => {
    const result = [];
    const specialChars = SPECIAL_CHARS_SYMBOLS;
    let currentWord = '';
  
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const isSpecialChar = specialChars.includes(char);
      const isWhitespace = /\s/.test(char);
      
      if (isWhitespace) {
        if (currentWord) {
          processAndPushWord(currentWord, result);
          currentWord = '';
        }
        result.push(char);
      } else if (isSpecialChar) {
        if (char === '!' || char === '¡') {
          if (currentWord && i > 0 && !/\s/.test(text[i - 1])) {
            currentWord += char;
            continue; 
          }
          else if (i + 1 < text.length && !/\s/.test(text[i + 1]) && !specialChars.includes(text[i + 1])) {
            currentWord = char; 
            continue;
          }
        }
        if (currentWord) {
          processAndPushWord(currentWord, result);
          currentWord = '';
        }
        result.push(char);
      } else {
        currentWord += char;
      }
    }
    
    if (currentWord) {
      processAndPushWord(currentWord, result);
    }
    
    return result.filter(word => word !== '');
  };

  const processAndPushWord = (word, result) => {
    if (!word) return;
    
    // Handle words with ellipsis as a single word
    if (word.includes('...') || /_\._/.test(word) || (word.includes('_') && !word.includes('.'))) {
      result.push(word);
      return;
    }
    const parts = [];
    let currentPart = '';
    
    for (let i = 0; i < word.length; i++) {
      const char = word[i];
      if (char === '"' || char === '“' || char === '”') {
        if (currentPart) {
          parts.push(currentPart);
          currentPart = '';
        }
        parts.push(char);
      } 
        // Split single dots as separate elements
        else if (char === '.') {
          const prevDigit = i > 0 && /\d/.test(word[i - 1]);
          const nextDigit = i + 1 < word.length && /\d/.test(word[i + 1]);
          const isDecimal = prevDigit && nextDigit;
          const prevUnderscore = i > 0 && word[i - 1] === '_';
          const nextUnderscore = i + 1 < word.length && word[i + 1] === '_';
          const endsWithUnderscore = prevUnderscore || (currentPart.length > 0 && currentPart[currentPart.length - 1] === '_');
          if (isDecimal || (endsWithUnderscore && nextUnderscore)) {
            currentPart += char;
          } else if (endsWithUnderscore && !nextUnderscore) {
            currentPart += char;
            if (currentPart) parts.push(currentPart);
            currentPart = '';
          } else if (!prevUnderscore && nextUnderscore) {
            if (currentPart) parts.push(currentPart);
            currentPart = '.';
          } else {
            if (currentPart) parts.push(currentPart);
            parts.push('.');
            currentPart = '';
          }
      } else {
        currentPart += char;
      }
    }

    if (currentPart) parts.push(currentPart);
    parts.forEach(part => result.push(part));
  };

  const handleWordClick = (word, startIndex) => {
    if (/^\s+$/.test(word)) return;
    try {
      const cleanWord = getCleanWord(word);
      if (!cleanWord) return;
      const endIndex = startIndex + cleanWord.length;
      const isAlreadyHighlighted = highlights.some(
        (h) => h.start === startIndex && h.end === endIndex
      );
      if (isAlreadyHighlighted) return;

      const newHighlight = { start: startIndex, end: endIndex, text: cleanWord, id: Date.now(), type: "highlight" };
      if (from === 'assignment') {
        const newHighlights = [...highlights, newHighlight].sort((a, b) => a.start - b.start);
        const hasOverlap = newHighlights.some((h, idx) => idx > 0 && h.start < newHighlights[idx - 1].end);
        if (hasOverlap) return;
        setHighlights(newHighlights);
      } else {
        setHighlights([newHighlight]);
      }
    } catch (error) {
      console.error("Selection error:", error);
      Sentry.captureException(`394 Error during handleWordClick: ${error}`);
    }
  };
  

  const renderText = () => {
    const words = splitIntoWords(text);
    let result = [];
    let currentIndex = 0;
    const sortedHighlights = [...highlights].sort((a, b) => a.start - b.start);
    let highlightIndex = 0;

    words.forEach((word) => {
      const startIndex = currentIndex;
      const endIndex = startIndex + word.length;

      while (highlightIndex < sortedHighlights.length && sortedHighlights[highlightIndex].end <= startIndex) {
        highlightIndex++;
      }

      const highlight = sortedHighlights[highlightIndex];
      const cleanWord = getCleanWord(word);
      const wordEndIndex = startIndex + cleanWord.length;
      const punctuationLength = word.length - cleanWord.length;

      if (highlight && highlight.start === startIndex && highlight.end === wordEndIndex) {
        result.push(
          <span
            key={`highlight-${highlight.id}`}
            className="fill-blanks-blue fill-in-the-blanks-preview cursor-pointer selected-word"
          >
            {cleanWord}
          </span>
        );
        if (stripPunctuation && punctuationLength > 0) {
          const punctuation = word.substring(cleanWord.length);
          result.push(
            <span
              key={`punctuation-${startIndex}`}
              onClick={() => handleWordClick(word, startIndex)}
              className="cursor-pointer hover:bg-yellow-100 px-0.5 rounded hover:underline"
            >
              {punctuation}
            </span>
          );
        }
        highlightIndex++;
      } else {
        if (/^\s+$/.test(word)) {
          result.push(word);
        } else {
          result.push(
            <span
              key={`word-${startIndex}`}
              onClick={() => handleWordClick(word, startIndex)}
              className="cursor-pointer hover:bg-yellow-100 px-0.5 rounded hover:underline"
            >
              {word}
            </span>
          );
        }
      }
      currentIndex = endIndex;
    });

    return result;
  };

  return <div className={`${from === "worksheet" ? "worksheet-highlight selected-text-worksheet" : "selected-text-container"}`}>{renderText()}</div>;
};

export default memo(SelectTextEditorSingleWord);